import React, {useState} from 'react';
import {LessonContentMChoiceAnswer} from '../../../../data/models/LessonContent/LessonContentBase';
import {microreelsListText} from '../microreelsListText';
import {formatReelDuration} from '../reelMeta';
import styles from './MicroreelQuestionEditor.module.css';

interface Props {
  reelTitle: string;
  reelIndex: number;
  initialQuestion: string;
  initialAnswers: LessonContentMChoiceAnswer[];
  durationSeconds?: number;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (question: string, answers: LessonContentMChoiceAnswer[]) => void;
}

const MIN_ANSWERS = 2;
const MAX_ANSWERS = 4;
const MAX_QUESTION_LENGTH = 200;
const MAX_ANSWER_LENGTH = 140;
// Answer counters stay hidden until the text approaches the limit.
const ANSWER_COUNTER_THRESHOLD = MAX_ANSWER_LENGTH - 30;

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M2.5 6.2L4.8 8.5L9.5 3.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MicroreelQuestionEditor = ({
  reelTitle,
  reelIndex,
  initialQuestion,
  initialAnswers,
  durationSeconds,
  isSaving,
  onCancel,
  onSave,
}: Props) => {
  const [question, setQuestion] = useState(initialQuestion);
  const [answers, setAnswers] = useState<LessonContentMChoiceAnswer[]>(
    initialAnswers.map(answer => ({...answer})),
  );

  const hasCorrect = answers.some(answer => answer.isCorrect);
  const normalizedAnswers = answers.map(answer =>
    answer.value.trim().toLowerCase(),
  );
  const duplicateValues = new Set(
    normalizedAnswers.filter(
      (value, index) => value && normalizedAnswers.indexOf(value) !== index,
    ),
  );
  const hasDuplicates = duplicateValues.size > 0;
  const canAddAnswer = answers.length < MAX_ANSWERS;
  const canSave =
    !isSaving &&
    question.trim().length > 0 &&
    question.length <= MAX_QUESTION_LENGTH &&
    answers.length >= MIN_ANSWERS &&
    answers.length <= MAX_ANSWERS &&
    answers.every(
      answer =>
        answer.value.trim().length > 0 &&
        answer.value.length <= MAX_ANSWER_LENGTH,
    ) &&
    !hasDuplicates &&
    hasCorrect;

  const setAnswerValue = (index: number, value: string) => {
    setAnswers(current =>
      current.map((answer, i) => (i === index ? {...answer, value} : answer)),
    );
  };

  const markCorrect = (index: number) => {
    setAnswers(current =>
      current.map((answer, i) => ({...answer, isCorrect: i === index})),
    );
  };

  const removeAnswer = (index: number) => {
    setAnswers(current => current.filter((_, i) => i !== index));
  };

  const addAnswer = () => {
    setAnswers(current =>
      current.length >= MAX_ANSWERS
        ? current
        : [...current, {value: '', isCorrect: false}],
    );
  };

  return (
    <div className={styles.body}>
      <div className={styles.phoneColumn}>
        <div className={styles.phoneFrame}>
          <div className={styles.phoneScreen}>
            <div className={styles.phoneHeader}>
              <p className={styles.phoneQuizTitle}>
                {microreelsListText.phoneQuizTitle}
              </p>
              <p className={styles.phoneQuizSubtitle}>
                {microreelsListText.phoneQuizSubtitle}
              </p>
              <span className={styles.phoneQuizUnderline} />
            </div>
            <div className={styles.phoneQuestionCard}>
              <p className={styles.phoneQuestion}>{question}</p>
              <div className={styles.phoneOptions}>
                {answers.map((answer, index) => (
                  <div
                    key={index}
                    className={`${styles.phoneOption} ${
                      answer.isCorrect ? styles.phoneOptionCorrect : ''
                    }`}>
                    {answer.isCorrect && (
                      <span className={styles.phoneOptionCheck}>
                        <CheckIcon />
                      </span>
                    )}
                    <span className={styles.phoneOptionText}>
                      {answer.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <span className={styles.phoneFinish}>
              {microreelsListText.phoneQuizFinish}
            </span>
          </div>
        </div>
        <p className={styles.phoneCaption}>
          {durationSeconds !== undefined
            ? microreelsListText.shownAtCaption(
                formatReelDuration(durationSeconds),
              )
            : microreelsListText.shownAtCaptionNoTime}
        </p>
      </div>

      <div className={styles.editorColumn}>
        <h1 className={styles.title}>{microreelsListText.editQuestionTitle}</h1>
        <p className={styles.subtitle}>
          {microreelsListText.editQuestionSubtitle(reelIndex, reelTitle)}
        </p>

        <label className={styles.fieldLabel} htmlFor="question-input">
          {microreelsListText.questionLabel}
        </label>
        <textarea
          id="question-input"
          className={styles.questionInput}
          value={question}
          rows={2}
          maxLength={MAX_QUESTION_LENGTH}
          disabled={isSaving}
          onChange={ev => setQuestion(ev.target.value)}
        />
        <span className={styles.questionCounter}>
          {question.length} / {MAX_QUESTION_LENGTH}
        </span>

        <p className={styles.fieldLabel}>{microreelsListText.answersLabel}</p>
        <p className={styles.answersHint}>{microreelsListText.answersHint}</p>

        <div className={styles.answersScroll}>
          <div className={styles.answersList}>
            {answers.map((answer, index) => (
              <div
                key={index}
                className={`${styles.answerRow} ${
                  answer.isCorrect ? styles.answerRowCorrect : ''
                } ${
                  duplicateValues.has(normalizedAnswers[index])
                    ? styles.answerRowDuplicate
                    : ''
                }`}>
                <button
                  className={`${styles.answerRadio} ${
                    answer.isCorrect ? styles.answerRadioCorrect : ''
                  }`}
                  onClick={() => markCorrect(index)}
                  disabled={isSaving}
                  title={microreelsListText.markCorrect}>
                  {answer.isCorrect && <CheckIcon />}
                </button>
                <input
                  className={styles.answerInput}
                  value={answer.value}
                  placeholder={microreelsListText.answerPlaceholder}
                  maxLength={MAX_ANSWER_LENGTH}
                  disabled={isSaving}
                  onChange={ev => setAnswerValue(index, ev.target.value)}
                />
                {answer.value.length >= ANSWER_COUNTER_THRESHOLD && (
                  <span className={styles.answerCounter}>
                    {answer.value.length} / {MAX_ANSWER_LENGTH}
                  </span>
                )}
                <button
                  className={styles.answerDelete}
                  onClick={() => removeAnswer(index)}
                  disabled={answers.length <= MIN_ANSWERS || isSaving}
                  title={microreelsListText.deleteAnswer}>
                  <span className="icon-trash" />
                </button>
              </div>
            ))}
          </div>

          <button
            className={styles.addAnswerButton}
            onClick={addAnswer}
            disabled={!canAddAnswer || isSaving}
            title={
              canAddAnswer
                ? undefined
                : microreelsListText.maxAnswersHint(MAX_ANSWERS)
            }>
            {microreelsListText.addAnswer}
          </button>
        </div>

        <div className={styles.footerCard}>
          <span
            className={`${styles.footerSummary} ${
              hasDuplicates ? styles.footerSummaryWarning : ''
            }`}>
            {hasDuplicates
              ? microreelsListText.duplicateAnswersWarning
              : hasCorrect
              ? microreelsListText.answersSummary(answers.length)
              : microreelsListText.answersSummaryNoCorrect(answers.length)}
          </span>
          <div className={styles.footerActions}>
            <button
              className={styles.cancelButton}
              onClick={onCancel}
              disabled={isSaving}>
              {microreelsListText.cancel}
            </button>
            <button
              className={styles.saveButton}
              onClick={() =>
                onSave(
                  question.trim(),
                  answers.map(answer => ({
                    ...answer,
                    value: answer.value.trim(),
                  })),
                )
              }
              disabled={!canSave}>
              {isSaving
                ? microreelsListText.savingQuestion
                : microreelsListText.saveQuestion}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MicroreelQuestionEditor;
